import React from 'react'
import moment from 'moment'
import { useToasts } from 'react-toast-notifications'
import { useApolloClient } from '@apollo/client'

import useDispatch from 'hooks/useDispatch'
import withRenderClientsideOnly from 'components/utils/withRenderClientsideOnly'
import useReduxSelector from '../../hooks/useReduxSelector'
import {
  useAddToBucketListMutation,
  useRemoveFromBucketListMutation,
  useAddAdventureToBookingMutation,
  useRemoveAdventureFromBookingMutation,
} from '../../hooks/useQueries'
import {
  closeSelectEscape,
  openSelectEscape,
  selectEscapeItemAddedTo,
  selectEscapeItemRemovedFrom,
} from './reduxSlice'
import { openCreateNewBucketList } from '../../actions/createNewBucketListActions'

import TrayModal from '../modals/TrayModal'
import LazyImage from '../display/LazyImage'
import HeartOnIcon from '../icons/BucketlistHeartOn'
import HeartOffIcon from '../icons/BucketlistHeartOff'
import CheckIcon from '../icons/Check'

import {
  makeItemKey,
  buildMutationOptionsAddToBucketList,
  buildMutationOptionsRemoveFromBucketList,
} from '../../dashboard/MyBucketlists/gql/getBucketListItemsIndexHelpers'
import {
  buildMutationOptionsAddItemToBooking,
  buildMutationOptionsRemoveItemFromBooking,
} from '../../dashboard/MyBookings/ItineraryBuilder/gql/getBookingAdventuresIndexHelpers'

import Loading from './Loading'
import {
  BookingItineraryButton,
  BucketListButton,
  Content,
  CreateNewBucketListButton,
  Dates,
  EscapeDetails,
  EscapeRow,
} from './display'
import useLazyEscapeIndexesQueries from './itemsIndex/useLazyEscapeIndexesQueries'
import useSelectEscapeData from './useData'

const formatDate = (date: string) => moment(date).format('D MMM YY')

const SelectEscape = () => {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const isOpen = useReduxSelector((state) => state.selectEscapeReducer.isOpen)
  const itemId = useReduxSelector((state) => state.selectEscapeReducer.itemId)
  const itemType = useReduxSelector(
    (state) => state.selectEscapeReducer.itemType
  )
  const source = useReduxSelector((state) => state.selectEscapeReducer.source)
  const apolloClient = useApolloClient()

  const [
    mutateAddToBucketListMutate,
    { loading: addToBucketListLoading },
  ] = useAddToBucketListMutation()
  const [
    mutateRemoveFromBucketList,
    { loading: removeFromBucketListLoading },
  ] = useRemoveFromBucketListMutation()
  const [
    mutateAddToBooking,
    { loading: addToBookingLoading },
  ] = useAddAdventureToBookingMutation()
  const [
    mutateRemoveFromBooking,
    { loading: removeFromBookingLoading },
  ] = useRemoveAdventureFromBookingMutation()

  const {
    bucketListItemsIndex,
    bookingAdventuresIndex,
  } = useLazyEscapeIndexesQueries(isOpen)

  const [
    { data: bucketListsData, loading: bucketListsLoading },
    { data: bookingsData, loading: bookingsLoading },
  ] = useSelectEscapeData(isOpen)

  const handleAddToBucketList = (bucketListId: string) => {
    const variables = {
      itemId,
      itemType,
      bucketListId,
      source,
    }
    mutateAddToBucketListMutate({
      variables,
      onCompleted: () => {
        addToast(`${itemType} added to bucket list`, { appearance: 'success' })

        // we put this in redux state so that other components can access it and perform any callbacks they need to
        dispatch(selectEscapeItemAddedTo({ bucketListId }))
      },
      onError: (error) => {
        addToast(error?.message, { appearance: 'error' })
      },
      ...buildMutationOptionsAddToBucketList({
        itemId,
        itemType,
        bucketListId,
      }),
    })
  }

  const handleRemoveFromBucketList = (bucketListId: string) => {
    const variables = {
      itemId,
      itemType,
      bucketListId,
      source,
    }
    mutateRemoveFromBucketList({
      variables,
      onCompleted: () => {
        addToast(`${itemType} removed from bucket list`, {
          appearance: 'success',
        })

        // we put this in redux state so that other components can access it and perform any callbacks they need to
        dispatch(selectEscapeItemRemovedFrom({ bucketListId }))
      },
      onError: (error) => {
        addToast(error?.message, { appearance: 'error' })
      },
      ...buildMutationOptionsRemoveFromBucketList({
        itemId,
        itemType,
        bucketListId,
      }),
    })
  }

  const handleAddToBooking = (bookingId: string) => {
    if (itemType !== 'Adventure') return

    mutateAddToBooking({
      variables: {
        bookingId,
        adventureId: itemId,
        source,
      },
      onCompleted: () => {
        addToast('Adventure added to escape')
      },
      onError: (error) => {
        addToast(error?.message, { appearance: 'error' })
      },
      ...buildMutationOptionsAddItemToBooking({
        itemId,
        bookingId,
        apolloClient,
      }),
    })
  }

  const handleRemoveFromBooking = (bookingId: string) => {
    if (itemType !== 'Adventure') return

    mutateRemoveFromBooking({
      variables: {
        bookingId,
        adventureId: itemId,
      },
      onCompleted: () => {
        addToast('Adventure removed from escape')
      },
      onError: (error) => {
        addToast(error?.message, { appearance: 'error' })
      },
      ...buildMutationOptionsRemoveItemFromBooking({
        itemId,
        bookingId,
        apolloClient,
      }),
    })
  }

  const handleClose = () => dispatch(closeSelectEscape())

  const handleCreateNewBucketListButtonClick = () => {
    const actionToReopenSelectEscape = openSelectEscape({
      itemId,
      itemType,
      source,
    })

    dispatch(
      openCreateNewBucketList({
        itemToAddOnCreation: {
          itemId,
          itemType,
          source,
        },
        actionToReplayOnClose: actionToReopenSelectEscape,
      })
    )
    dispatch(closeSelectEscape())
  }

  if (itemType == null || itemId == null) {
    return null
  }

  const bookingsIds =
    bookingAdventuresIndex && bookingAdventuresIndex[itemId]
      ? bookingAdventuresIndex[itemId]
      : []
  const allBookings = bookingsData?.currentUser?.upcomingBookings

  const bookingsWithCurrentItemInItinerary =
    bookingAdventuresIndex?.[itemId] || []

  const itemKey = makeItemKey({ itemId, itemType })
  const bucketListsWithCurrentItem = bucketListItemsIndex?.[itemKey] || []

  const allBucketLists = bucketListsData?.currentUser?.bucketLists

  return (
    <TrayModal
      title="Select a list"
      isOpen={isOpen}
      onRequestClose={handleClose}
    >
      <Content>
        {itemType === 'Adventure' && bookingsIds && (
          <>
            {allBookings?.length > 0 && <h2>Upcoming Escapes</h2>}
            {bookingsLoading || !allBookings ? (
              <Loading type="bookings" />
            ) : (
              allBookings?.map((booking) => {
                const isInBookingItinerary = bookingsWithCurrentItemInItinerary?.includes(
                  booking.id
                )

                const handleToggle = () => {
                  if (isInBookingItinerary) {
                    handleRemoveFromBooking(booking.id)
                  } else {
                    handleAddToBooking(booking.id)
                  }
                }

                const imageUrl =
                  booking?.story?.promoImage?.medium ??
                  booking.listing.promoImage?.medium

                return (
                  <EscapeRow
                    key={`booking:${booking.id}`}
                    data-testid="upcoming-booking"
                  >
                    <LazyImage data-src={imageUrl} rounded />
                    <EscapeDetails>
                      <span>{booking.listing.title}</span>
                      <Dates>
                        {formatDate(booking.startDate)} -{' '}
                        {formatDate(booking.endDate)}
                      </Dates>
                    </EscapeDetails>
                    <BookingItineraryButton
                      aria-label={
                        isInBookingItinerary
                          ? 'Remove from itinerary'
                          : 'Add to itinerary'
                      }
                      filled={isInBookingItinerary}
                      onClick={handleToggle}
                      disabled={addToBookingLoading || removeFromBookingLoading}
                    >
                      <div>
                        <CheckIcon />
                      </div>
                    </BookingItineraryButton>
                  </EscapeRow>
                )
              })
            )}
          </>
        )}

        <h2>Bucket lists</h2>
        {bucketListsLoading || !allBucketLists ? (
          <Loading type="bucketLists" />
        ) : (
          allBucketLists?.map((bucketList) => {
            const inBucketList = bucketListsWithCurrentItem?.includes(
              bucketList.id
            )

            const handleToggle = () => {
              if (inBucketList) {
                handleRemoveFromBucketList(bucketList.id)
              } else {
                handleAddToBucketList(bucketList.id)
              }
            }

            return (
              <EscapeRow
                key={`bucketList:${bucketList.id}`}
                data-testid="bucket-list"
              >
                <LazyImage data-src={bucketList.theme?.image?.medium} rounded />
                <EscapeDetails>
                  <span>{bucketList.name}</span>
                </EscapeDetails>
                <BucketListButton
                  aria-label={
                    inBucketList
                      ? 'Remove from bucket list'
                      : 'Add to bucket list'
                  }
                  isActive={inBucketList}
                  onClick={handleToggle}
                  disabled={
                    addToBucketListLoading || removeFromBucketListLoading
                  }
                >
                  <HeartOnIcon />
                  <HeartOffIcon />
                </BucketListButton>
              </EscapeRow>
            )
          })
        )}

        <CreateNewBucketListButton
          onClick={handleCreateNewBucketListButtonClick}
          ghost
          uppercase={false}
        >
          Create a new bucket list
        </CreateNewBucketListButton>
      </Content>
    </TrayModal>
  )
}

export default withRenderClientsideOnly(SelectEscape)
